import * as React from "react";
import { Box, Button, CircularProgress, Divider, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import Accordion from "@mui/material/Accordion";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import { MenuPopoverProps } from "../../types/Popover";
import { useEffect, useRef, useState } from "react";
import { PopoverMenu, StyledFormGroup } from "./style";
import { useTheme } from "styled-components";
import { staticFormats, staticPlatforms } from "../../utils/constants";
import CheckBoxGroup from "../commonComponents/checkboxGroup";
import FilterSearchbox from "../commonComponents/filterSearchbox";
import { useDispatch, useSelector } from "react-redux";
import {
  TagsListsSelector,
  boardFilterSearchTextSelector,
  boardItemsSelector,
  tagFilterSearchTextSelector,
} from "../../store/metadata/selector";
import {
  discoveryBrandsSelector,
  discoveryBrandsParamsSelector,
  discoveryPageSearchSelector,
  discoveryFavouriteBrandFilterSelector,
  isLoadingSelector,
} from "../../store/discovery/selector";
import { DiscoverySagaActions } from "../../store/discovery/sagas";
import { DiscoveryReducerAction } from "../../store/discovery/slice";


import { BoardIcon, TagsIcon } from "../../assets/Icons";
import { listType } from "../../store/metadata/types";
import { MetaDataReducerAction } from "../../store/metadata/slice";
import { MetaDataSagaActions } from "../../store/metadata/sagas";
import { notify, ToastTypes } from "../../utils/helpers";
import { TopBrandLists } from "../../services/BrandsService";
// removed unused discoveryItemsSelector import

const NoDataFound = () => {
  const theme = useTheme();
  return (
    <Box
      display={"flex"}
      justifyContent={"center"}
      margin={"0.5rem"}
      color={theme.colors.black2}
    >
      No data found.
    </Box>
  );
};

const AccordionWrapper = styled((props: AccordionProps) => (
  <Accordion disableGutters elevation={0} {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    sx={{ background: "white !important", padding: "1rem", minHeight: "3rem" }}
    expandIcon={
      <ArrowForwardIosSharpIcon sx={{ fontSize: "0.8rem", color: "#414042" }} />
    }
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, .05)"
      : "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    margin: `0 0 0 ${theme.spacing(1)}`,
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: "0.5rem 1rem 1rem",
  backgroundColor: "#F9F9FA",
}));

const SelectAllHeaderWrapper = ({
  selectAllHandler,
  selectedAll,
}: {
  selectAllHandler: () => void;
  selectedAll: boolean;
}) => {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: "center",
        justifyContent: "space-between",
        paddingBlock: "0.75rem",
      }}
    >
      <Typography
        fontSize={"0.938rem"}
        fontWeight={500}
        color={theme.colors.neutral50}
      >
        Show from
      </Typography>
      <Button
        sx={{
          color: theme.colors.link,
          fontWeight: 600,
          fontSize: "0.938rem",
          textTransform: "capitalize",
          padding: 0,
        }}
        onClick={selectAllHandler}
      >
        {selectedAll ? "Deselect All" : "Select All"}
      </Button>
    </Stack>
  );
};

// TODO need to create component for brand, boards, format, and platform listing
const MenuPopover = ({
  anchorEl,
  handlePopoverClose,
  handleFilter,
  selectedBrands,
  selectedPlatforms,
  renderBoardFilter,
  selectedBoards,
  userId,
  renderTagFilter,
  selectedTags,
}: MenuPopoverProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState<string | false>("");
  const [selectedFormats, setSelectedFormats] = useState<number[]>([]);
  const isFetchingMoreBrandsRef = useRef<boolean>(false);
  const brandSearchTimeoutRef = useRef<number | null>(null);
  const [localBrandSearch, setLocalBrandSearch] = useState<string>("");
  const [localBrandList, setLocalBrandList] = useState<any[]>([]);
  const [localTotal, setLocalTotal] = useState<number>(0);
  const [localSkip, setLocalSkip] = useState<number>(0);
  const [localLoading, setLocalLoading] = useState<boolean>(false);

  const boardLists = useSelector(boardItemsSelector);
  const tagLists = useSelector(TagsListsSelector);
  const boardFilterSearchText = useSelector(boardFilterSearchTextSelector);
  const tagFilterSearchText = useSelector(tagFilterSearchTextSelector);

  // Discovery brands and search state
  const discoveryBrands = useSelector(discoveryBrandsSelector);
  const discoveryBrandsParams = useSelector(discoveryBrandsParamsSelector);
  const discoverySearch = useSelector(discoveryPageSearchSelector);
  const favouriteBrandFilterEnabled = useSelector(
    discoveryFavouriteBrandFilterSelector
  );

  const hasMoreBrands =
    (discoveryBrands?.total ?? 0) > (discoveryBrands?.data?.length ?? 0);
  const discoveryLoading = useSelector(isLoadingSelector);

  const brandListForCheckbox = React.useMemo(() => {
    const data = localBrandSearch.trim() !== "" ? localBrandList : (discoveryBrands?.data ?? []);
    return data.map((b: any) => ({ _id: b?._id, name: b?.name, logo: b?.logo }));
  }, [localBrandSearch, localBrandList, discoveryBrands?.data]);

  useEffect(() => {
    // reset local fetching guard when store updates (new page loaded)
    isFetchingMoreBrandsRef.current = false;
  }, [discoveryBrandsParams?.skip, discoveryBrands?.data?.length]);

  const handleBrandListScroll: React.UIEventHandler<HTMLDivElement> = (
    event
  ) => {
    const target = event.currentTarget;
    const nearBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - 48;

    if (
      nearBottom &&
      ((localBrandSearch.trim() !== "" && localTotal > localBrandList.length) ||
        (localBrandSearch.trim() === "" && hasMoreBrands)) &&
      !isFetchingMoreBrandsRef.current
    ) {
      isFetchingMoreBrandsRef.current = true;
      if (localBrandSearch.trim() !== "") {
        TopBrandLists({
          skip: localSkip,
          limit: discoveryBrandsParams.limit,
          userId: userId as any,
          search: String(localBrandSearch ?? ""),
          favourite: Boolean(favouriteBrandFilterEnabled),
        })
          .then((res: any) => {
            if (res?.status === 200) {
              setLocalBrandList((prev) => [...prev, ...(res?.data?.data ?? [])]);
              setLocalSkip(localSkip + discoveryBrandsParams.limit);
            }
          })
          .finally(() => {
            isFetchingMoreBrandsRef.current = false;
          });
      } else {
        dispatch(
          DiscoverySagaActions.fetchDiscoveryTopBrandList({
            ...discoveryBrandsParams,
            skip: discoveryBrandsParams.skip + discoveryBrandsParams.limit,
            userId: userId as any,
            search: "",
            favourite: Boolean(favouriteBrandFilterEnabled),
          })
        );
      }
    }
  };

  const handleBrandSearch = () => {
    if (localBrandSearch.trim() === "") {
      setLocalBrandList([]);
      setLocalTotal(0);
      setLocalSkip(0);
      return;
    }
    setLocalLoading(true);
    TopBrandLists({
      skip: 0,
      limit: discoveryBrandsParams.limit,
      userId: userId as any,
      search: String(localBrandSearch ?? ""),
      favourite: Boolean(favouriteBrandFilterEnabled),
    })
      .then((res: any) => {
        if (res?.status === 200) {
          setLocalBrandList(res?.data?.data ?? []);
          setLocalTotal(res?.data?.total ?? 0);
          setLocalSkip(discoveryBrandsParams.limit);
        }
      })
      .finally(() => setLocalLoading(false));
  };

  const handleBrandSearchOnChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputText = event.target.value;
    setLocalBrandSearch(inputText);

    if (brandSearchTimeoutRef.current) {
      window.clearTimeout(brandSearchTimeoutRef.current);
    }

    brandSearchTimeoutRef.current = window.setTimeout(() => {
      if (inputText.trim() === "") {
        setLocalBrandList([]);
        setLocalTotal(0);
        setLocalSkip(0);
        return;
      }
      setLocalLoading(true);
      TopBrandLists({
        skip: 0,
        limit: discoveryBrandsParams.limit,
        userId: userId as any,
        search: String(inputText ?? ""),
        favourite: Boolean(favouriteBrandFilterEnabled),
      })
        .then((res: any) => {
          if (res?.status === 200) {
            setLocalBrandList(res?.data?.data ?? []);
            setLocalTotal(res?.data?.total ?? 0);
            setLocalSkip(discoveryBrandsParams.limit);
          }
        })
        .finally(() => setLocalLoading(false));
    }, 500);
  };

  const handleBrandSearchKeyDown: React.KeyboardEventHandler<
    HTMLInputElement
  > = (event) => {
    // prevent accordion toggle while typing
    event.stopPropagation();
  };

  useEffect(() => {
    return () => {
      if (brandSearchTimeoutRef.current) {
        window.clearTimeout(brandSearchTimeoutRef.current);
      }
    };
  }, []);

  const handleBoardSearchOnChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(
      MetaDataReducerAction.setBoardFilterSearchText(event.target.value)
    );
    if (event.target.value === "") {
      dispatch(
        MetaDataSagaActions.fetchBoardList({ search: boardFilterSearchText })
      );
    }
  };

  const handleBoardSearch = () => {
    dispatch(
      MetaDataSagaActions.fetchBoardList({ search: boardFilterSearchText })
    );
  };

  const handleBoardSearchKeyDown: React.KeyboardEventHandler<
    HTMLInputElement
  > = (event) => {
    event.stopPropagation();
    if (event.key === "Enter") handleBoardSearch();
  };

  const handleSelectCheckbox = ({
    checked,
    list,
    setSelected,
    selected,
    selectAll,
    id,
  }: {
    checked: boolean;
    list: any;
    setSelected?: any;
    selected: number[];
    selectAll?: boolean;
    id?: string;
  }) => {
    if (checked) {
      selectAll
        ? setSelected(list.map((n: any) => n._id))
        : setSelected([...selected, id!]);
    } else {
      selectAll
        ? setSelected([])
        : setSelected(selected.filter((item) => item.toString() !== id));
    }
  };

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  const handleChangeLists = (
    selectAll: boolean,
    checked: boolean,
    stateName: string,
    list: listType[],
    id?: string
  ) => {
    if (handleFilter) {
      handleFilter(selectAll, checked, stateName, list, id);
    }
  };

  const handleTagSearch = () => {
    dispatch(
      MetaDataSagaActions.fetchTagLists({ searchValue: tagFilterSearchText })
    );
  };

  const handleTagSearchOnChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(MetaDataReducerAction.setTagFilterSearchText(event.target.value));
    if (event.target.value === "") {
      dispatch(
        MetaDataSagaActions.fetchTagLists({
          searchValue: boardFilterSearchText,
        })
      );
    }
  };

  const handleTagSearchKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (
    event
  ) => {
    event.stopPropagation();
    if (event.key === "Enter") handleTagSearch();
  };

  return (
    <PopoverMenu
      anchorEl={anchorEl}
      keepMounted
      open={Boolean(anchorEl)}
      onClose={handlePopoverClose}
      variant="selectedMenu"
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      sx={{
        ".MuiMenu-paper": {
          borderRadius: "0.938rem",
          overflow: "hidden",
          width: "24.375rem",
        },
      }}
    >
      <AccordionWrapper
        expanded={expanded === "panel1"}
        onChange={handleChange("panel1")}
      >
        <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
          <Typography
            fontSize={"1.125rem"}
            fontWeight={expanded === "panel1" ? 700 : 500}
          >
            Brand
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FilterSearchbox
            placeholder="Search for brands..."
            value={localBrandSearch}
            handleChange={handleBrandSearchOnChange}
            handleKeyDown={handleBrandSearchKeyDown}
            handleSearchIconClick={handleBrandSearch}
          />
          {(brandListForCheckbox?.length ?? 0) === 0 ? (
            localBrandSearch.trim() !== "" ? (
              localLoading ? (
                <Box display={"flex"} justifyContent={"center"} py={2}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <NoDataFound />
              )
            ) : discoveryLoading ? (
              <Box display={"flex"} justifyContent={"center"} py={2}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <NoDataFound />
            )
          ) : (
            <>
              <SelectAllHeaderWrapper
                selectedAll={
                  (brandListForCheckbox?.length ?? 0) ===
                  (selectedBrands?.length ?? 0)
                }
                selectAllHandler={() =>
                  handleChangeLists(
                    true,
                    !(
                      (brandListForCheckbox?.length ?? 0) ===
                      (selectedBrands?.length ?? 0)
                    ),
                    "selectedPageIds",
                    brandListForCheckbox as unknown as listType[]
                  )
                }
              />
              <Divider
                sx={{ color: theme.colors.border3, marginInline: "-1rem" }}
              />
              <Box
                sx={{
                  maxHeight: "16rem",
                  overflowY: "auto",
                  overflowX: "hidden",
                  paddingRight: "0.25rem",
                }}
                onScroll={handleBrandListScroll}
              >
                <StyledFormGroup>
                  <CheckBoxGroup
                    list={brandListForCheckbox as unknown as listType[]}
                    selected={selectedBrands}
                    onChangeHandler={(
                      event: React.ChangeEvent<HTMLInputElement>,
                      id: string
                    ) =>
                      handleChangeLists(
                        false,
                        event.target.checked,
                        "selectedPageIds",
                        brandListForCheckbox as unknown as listType[],
                        id
                      )
                    }
                  />
                </StyledFormGroup>
                {isFetchingMoreBrandsRef.current && (
                  <Box display={"flex"} justifyContent={"center"} py={1.5}>
                    <CircularProgress size={16} />
                  </Box>
                )}
              </Box>
            </>
          )}
        </AccordionDetails>
      </AccordionWrapper>

      {renderBoardFilter && (
        <AccordionWrapper
          expanded={expanded === "panel2"}
          onChange={handleChange("panel2")}
        >
          <AccordionSummary aria-controls="panel2d-content" id="panel2d-header">
            <Typography
              fontSize={"1.125rem"}
              fontWeight={expanded === "panel2" ? 700 : 500}
            >
              Board
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FilterSearchbox
              placeholder="Search for boards..."
              value={boardFilterSearchText}
              handleChange={handleBoardSearchOnChange}
              handleKeyDown={handleBoardSearchKeyDown}
              handleSearchIconClick={handleBoardSearch}
            />
            {boardLists.length === 0 ? (
              <NoDataFound />
            ) : (
              <>
                <SelectAllHeaderWrapper
                  selectedAll={boardLists?.length === selectedBoards?.length}
                  selectAllHandler={() =>
                    handleChangeLists(
                      true,
                      !(boardLists?.length === selectedBoards?.length),
                      "selectedBoardIds",
                      boardLists
                    )
                  }
                />
                <Divider
                  sx={{ color: theme.colors.border3, marginInline: "-1rem" }}
                />
                <StyledFormGroup>
                  <CheckBoxGroup
                    list={boardLists}
                    selected={selectedBoards}
                    staticIcon={<BoardIcon fill={theme.colors.gray1} />}
                    onChangeHandler={(
                      event: React.ChangeEvent<HTMLInputElement>,
                      id: string
                    ) =>
                      handleChangeLists(
                        false,
                        event.target.checked,
                        "selectedBoardIds",
                        boardLists,
                        id
                      )
                    }
                  />
                </StyledFormGroup>
              </>
            )}
          </AccordionDetails>
        </AccordionWrapper>
      )}

      {renderTagFilter && (
        <AccordionWrapper
          expanded={expanded === "tags"}
          onChange={handleChange("tags")}
        >
          <AccordionSummary aria-controls="tags-content" id="tags-header">
            <Typography
              fontSize={"1.125rem"}
              fontWeight={expanded === "tags" ? 700 : 500}
            >
              Tags
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FilterSearchbox
              placeholder="Search for tags..."
              value={tagFilterSearchText}
              handleChange={handleTagSearchOnChange}
              handleKeyDown={handleTagSearchKeyDown}
              handleSearchIconClick={handleTagSearch}
            />
            {tagLists.length === 0 ? (
              <NoDataFound />
            ) : (
              <>
                <SelectAllHeaderWrapper
                  selectedAll={tagLists?.length === selectedTags?.length}
                  selectAllHandler={() =>
                    handleChangeLists(
                      true,
                      !(tagLists?.length === selectedTags?.length),
                      "selectedTagIds",
                      tagLists
                    )
                  }
                />
                <Divider
                  sx={{ color: theme.colors.border3, marginInline: "-1rem" }}
                />
                <StyledFormGroup>
                  <CheckBoxGroup
                    list={tagLists}
                    selected={selectedTags}
                    staticIcon={<TagsIcon />}
                    onChangeHandler={(
                      event: React.ChangeEvent<HTMLInputElement>,
                      id: string
                    ) =>
                      handleChangeLists(
                        false,
                        event.target.checked,
                        "selectedTagIds",
                        tagLists,
                        id
                      )
                    }
                  />
                </StyledFormGroup>
              </>
            )}
          </AccordionDetails>
        </AccordionWrapper>
      )}

      <AccordionWrapper
        expanded={expanded === "panel4"}
        onChange={handleChange("panel4")}
      >
        <AccordionSummary aria-controls="panel4d-content" id="panel4d-header">
          <Typography
            fontSize={"1.125rem"}
            fontWeight={expanded === "panel4" ? 700 : 500}
          >
            Platform
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SelectAllHeaderWrapper
            selectedAll={staticPlatforms?.length === selectedPlatforms?.length}
            selectAllHandler={() =>
              handleChangeLists(
                true,
                !(staticPlatforms?.length === selectedPlatforms?.length),
                "selectedPlatformIds",
                staticPlatforms
              )
            }
          />
          <Divider
            sx={{ color: theme.colors.border3, marginInline: "-1rem" }}
          />
          <StyledFormGroup padding="0 1.25rem">
            <CheckBoxGroup
              list={staticPlatforms}
              selected={selectedPlatforms}
              onChangeHandler={(
                event: React.ChangeEvent<HTMLInputElement>,
                id: string
              ) =>
                handleChangeLists(
                  false,
                  event.target.checked,
                  "selectedPlatformIds",
                  staticPlatforms,
                  id
                )
              }
            />
          </StyledFormGroup>
        </AccordionDetails>
      </AccordionWrapper>
    </PopoverMenu>
  );
};

export default MenuPopover;
